import { SHARE_PATH, type StoreCard } from '@boardflight/shared';
import { match } from '../router';

/**
 * Where the storefront lives (`docs/off-board-plan.md` §2.2), and nothing else — no React, no
 * `window`, so the whole of it is checked in Node.
 *
 * The library is one set of pages drawn by two shells: a browser tab and, later, the desktop app's
 * window. Both ask this file what a path means, so a link built in one place and a route matched
 * in another can never disagree about a URL.
 */

export const LIBRARY_PATH = '/library';

/**
 * The library's front page, as the address to send somebody to.
 *
 * The site's root is the landing page for a stranger and **the library for an account**: nobody
 * signed in should have to read `/library` in their address bar to be at the front of the thing
 * they came for. `/library` still opens, for everybody, and is the same page — it is the address a
 * stranger browses at, and the only one the desktop app's window may show, which is why the app
 * keeps it.
 */
export function libraryHome(signedIn: boolean, inApp: boolean): string {
  return signedIn && !inApp ? '/' : LIBRARY_PATH;
}
export const SEARCH_PATH = `${LIBRARY_PATH}/search`;
/** My Games: everything the signed-in account may install (`docs/off-board-plan.md` §5.8). */
export const MINE_PATH = `${LIBRARY_PATH}/mine`;
/** My Boards: the Boards connected to the signed-in account, and what each is set to do (§5.5). */
export const BOARDS_PATH = `${LIBRARY_PATH}/boards`;
/**
 * The onboarding (§5.6). Under `/library`, so the desktop app's window may show it: the shell
 * holds navigation to the storefront's routes (`is_storefront_route` in the app's `site.rs`).
 */
export const WELCOME_PATH = `${LIBRARY_PATH}/welcome`;
/** The account's own settings, on this side of the site (`account/AccountPage.tsx`). */
export const ACCOUNT_PATH = `${LIBRARY_PATH}/account`;
const GAME_PATTERN = `${LIBRARY_PATH}/game/:appId`;

/** Your own player profile (`docs/achievements-plan.md` §12a.8), at the site's short `/u/`. */
export const OWN_PROFILE_PATH = '/u/me';
/** Somebody's public profile. Usernames are lower case; the page sends any other spelling there. */
export function profilePath(username: string): string {
  return `/u/${encodeURIComponent(username.toLowerCase())}`;
}
/** Where a phone lands from a game's QR code to add that game to a profile (§4.3). */
export const KEEP_PATTERN = '/keep/:code';

/** A game's page by its id — the page for a game with no short link to its name. */
export function gamePath(appId: string): string {
  return `${LIBRARY_PATH}/game/${encodeURIComponent(appId)}`;
}

/**
 * Where a card goes: the game's own short link, else its page by id.
 *
 * The short link is the address worth landing on — it is the one a player copies out of the
 * address bar, and the one the web Worker writes a preview for (`docs/store-plan.md` §14). A card
 * without a code is only ever an API from before codes existed, and it still has a page.
 */
export function gameHref(card: Pick<StoreCard, 'id' | 'shareCode'>): string {
  return card.shareCode ? `${SHARE_PATH}/${encodeURIComponent(card.shareCode)}` : gamePath(card.id);
}

export type StorefrontRoute =
  | { page: 'front' }
  | { page: 'search' }
  | { page: 'mine' }
  | { page: 'boards' }
  | { page: 'welcome' }
  | { page: 'account' }
  | { page: 'game'; appId: string }
  /** `/u/me`, or `/u/<username>`. */
  | { page: 'profile'; who: { own: true } | { own: false; username: string } }
  /** `/keep/<code>`: confirming a game's link code. */
  | { page: 'keep'; code: string }
  /** Under `/library`, and nothing this build draws. */
  | { page: 'missing' };

/**
 * Whether a path is the storefront's to answer — `/library` and everything under it, and the two
 * player-profile pages at the site's short addresses, `/u/…` and `/keep/…`.
 */
export function isStorefrontPath(path: string): boolean {
  return path === LIBRARY_PATH || path.startsWith(`${LIBRARY_PATH}/`) || path.startsWith('/u/') || path.startsWith('/keep/');
}

/**
 * The page a path names, or null for a path that is not the storefront's.
 *
 * Everything under `/library` is answered here rather than falling through to the dashboard: a
 * mistyped library address is a library page that does not exist, not a sign-in form.
 */
export function storefrontRoute(path: string): StorefrontRoute | null {
  if (!isStorefrontPath(path)) return null;
  if (match(LIBRARY_PATH, path)) return { page: 'front' };
  if (match(SEARCH_PATH, path)) return { page: 'search' };
  if (match(MINE_PATH, path)) return { page: 'mine' };
  if (match(BOARDS_PATH, path)) return { page: 'boards' };
  if (match(WELCOME_PATH, path)) return { page: 'welcome' };
  if (match(ACCOUNT_PATH, path)) return { page: 'account' };
  const game = match(GAME_PATTERN, path);
  if (game?.appId) return { page: 'game', appId: game.appId };
  const profile = match('/u/:username', path);
  if (profile?.username) {
    return { page: 'profile', who: profile.username === 'me' ? { own: true } : { own: false, username: profile.username } };
  }
  const keep = match(KEEP_PATTERN, path);
  if (keep?.code) return { page: 'keep', code: keep.code };
  return { page: 'missing' };
}

/* ------------------------------------------------------------------------------ search */

/** What the search page is showing, as its address says it. */
export interface SearchTerms {
  /** Trimmed, and empty for "no words" — never null, so two empties compare equal. */
  q: string;
  /** One genre word, lower case, or null for all of them. */
  genre: string | null;
}

/** The server's own limits (`StoreSearchQuery`): a longer value is refused, so it is never sent. */
export const QUERY_MAX = 100;
const GENRE_MAX = 40;

function cleanGenre(value: string | null | undefined): string | null {
  const genre = (value ?? '').trim().toLowerCase().slice(0, GENRE_MAX);
  return genre === '' ? null : genre;
}

/** The terms in a query string — `window.location.search`, with or without its `?`. */
export function parseSearch(search: string): SearchTerms {
  const params = new URLSearchParams(search);
  return {
    q: (params.get('q') ?? '').trim().slice(0, QUERY_MAX),
    genre: cleanGenre(params.get('genre')),
  };
}

/**
 * The search page's address for some terms. Nothing chosen is the bare path, so "all games" has
 * one address rather than three spellings of it.
 */
export function searchHref(terms: Partial<SearchTerms> = {}): string {
  const params = new URLSearchParams();
  const q = (terms.q ?? '').trim().slice(0, QUERY_MAX);
  const genre = cleanGenre(terms.genre);
  if (q) params.set('q', q);
  if (genre) params.set('genre', genre);
  const query = params.toString();
  return query ? `${SEARCH_PATH}?${query}` : SEARCH_PATH;
}
